import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { RegionService } from 'src/app/services/region/region.service';
import { RegionModalService } from 'src/app/services/region-modal/region-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-region',
  templateUrl: './add-edit-region.component.html',
  styleUrls: ['./add-edit-region.component.scss']
})
export class AddEditRegionComponent extends DestructorComponent implements OnInit {

  @Output() regionAdded = new EventEmitter<void>();
  @Output() regionUpdated = new EventEmitter<void>();

  regionForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedRegion: any = null;

  constructor(
    private fb: FormBuilder,
    private _regionService: RegionService,
    private _regionModalService: RegionModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.regionForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_region_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceRegion = this._regionModalService.getSelectedRegion();
        if (serviceRegion) {
          this.isEditMode = true;
          this.selectedRegion = serviceRegion;
          this.populateForm(this.selectedRegion);
        } else {
          this.isEditMode = false;
          this.selectedRegion = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._regionModalService.clearSelectedRegion();
        this.resetForm();
      });
    }
  }



  private populateForm(region: any): void {
    if (region) {
      this.regionForm.patchValue({
        name: region.name || ''
      });
    }
  }

  private resetForm(): void {
    this.regionForm.reset();
    this.regionForm.patchValue({
      name: ''
    });
    this.isLoading = false;
  }

  // Getter for easy access to form controls
  get name() { return this.regionForm.get('name'); }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  onSubmit(): void {
    if (this.regionForm.invalid) {
      this.markFormGroupTouched(this.regionForm);
      return;
    }

    this.isLoading = true;

    const formData = {
      name: this.regionForm.get('name')?.value
    };

    if (this.isEditMode) {
      this.updateRegion(formData);
    } else {
      this.addRegion(formData);
    }
  }

  private addRegion(formData: any): void {
    this.subscriptions.push(
      this._regionService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Region added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.regionAdded.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add region.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error adding region:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while adding the region.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private updateRegion(formData: any): void {
    const regionId = this.selectedRegion._id || this.selectedRegion.id;
    this.subscriptions.push(
      this._regionService.UpdateData(regionId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Region updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.regionUpdated.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update region.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error updating region:', error);
          this.isLoading = false;

          Swal.fire({
            text: error.error?.message || "Sorry, looks like there are some errors detected, please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_region_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }


}
